"use client"

import { useState } from "react"
import Button from "@/components/ui/Button"
import { Calculator, Copy, Printer, Share2 } from "lucide-react"

type ReorderResult = {
   reorderPoint: number
   safetyStock: number
   maxStockLevel: number
   demand: number
   leadTime: number
}

type FieldId = "demand" | "leadTime" | "safetyDays"

const FIELDS: { id: FieldId; label: string; tooltip: string; placeholder: string }[] = [
   {
      id: "demand",
      label: "Average Daily Demand (units)",
      tooltip: "How much of this ingredient you use per day on average.",
      placeholder: "e.g. 50",
   },
   {
      id: "leadTime",
      label: "Lead Time (days)",
      tooltip: "Number of days from placing an order to it arriving in your kitchen.",
      placeholder: "e.g. 4",
   },
   {
      id: "safetyDays",
      label: "Safety Stock (days)",
      tooltip: "Extra days of inventory buffer you want to hold to prevent stockouts.",
      placeholder: "e.g. 4",
   },
]

function TooltipButton({ tip }: { tip: string }) {
   return (
      <span className="group relative">
         <button
            type="button"
            className="flex h-4 w-4 items-center justify-center rounded-full border border-gray-400 text-[9px] font-bold text-gray-500 hover:border-[#196EB9] hover:text-[#196EB9]"
         >
            ?
            <span className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 hidden w-48 -translate-x-1/2 rounded-lg bg-gray-900 px-3 py-2 text-xs font-normal text-white group-hover:block">
               {tip}
            </span>
         </button>
      </span>
   )
}

function NumberInput({
   id,
   label,
   tooltip,
   placeholder,
   value,
   onChange,
}: {
   id: string
   label: string
   tooltip: string
   placeholder: string
   value: string
   onChange: (val: string) => void
}) {
   return (
      <div className="space-y-2">
         <label
            htmlFor={id}
            className="flex items-center gap-1.5 text-[14px] font-bold uppercase  text-gray-700"
         >
            {label} <span className="text-[#EB5A23]">*</span>
            <TooltipButton tip={tooltip} />
         </label>
         <input
            id={id}
            type="number"
            min={0}
            step="any"
            placeholder={placeholder}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full rounded-xl border border-gray-200 px-4 py-3.5 text-base text-gray-900 placeholder-gray-300 outline-none transition focus:border-[#196EB9] focus:ring-2 focus:ring-[#196EB9]/20"
         />
      </div>
   )
}

function ResultCell({ label, value, sub }: { label: string; value: string; sub: string }) {
   return (
      <div className="flex flex-col items-center gap-1 border-r border-gray-100 px-4 py-4 text-center last:border-r-0">
         <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{label}</span>
         <span className="font-serif text-xl font-bold text-gray-900">{value}</span>
         <span className="text-[10px] text-gray-400">{sub}</span>
      </div>
   )
}

export default function ReorderCalculatorForm() {
   const [values, setValues] = useState<Record<FieldId, string>>({ demand: "", leadTime: "", safetyDays: "" })
   const [result, setResult] = useState<ReorderResult | null>(null)
   const [error, setError] = useState<string | null>(null)
   const [resultCopied, setResultCopied] = useState(false)
   const [shareCopied, setShareCopied] = useState(false)

   function handleChange(id: FieldId, val: string) {
      setValues((prev) => ({ ...prev, [id]: val }))
      setError(null)
   }

   function calculate() {
      const demand = parseFloat(values.demand)
      const leadTime = parseFloat(values.leadTime)
      const safetyDays = parseFloat(values.safetyDays)

      if (isNaN(demand) || isNaN(leadTime) || isNaN(safetyDays)) {
         setError("Please fill in all fields with valid numbers.")
         return
      }
      if (demand < 0 || leadTime < 0 || safetyDays < 0) {
         setError("Values cannot be negative.")
         return
      }

      const safetyStock = demand * safetyDays
      const leadTimeDemand = demand * leadTime
      const reorderPoint = leadTimeDemand + safetyStock
      const maxStockLevel = reorderPoint + leadTimeDemand

      setResult({ reorderPoint, safetyStock, maxStockLevel, demand, leadTime })
      setError(null)
   }

   function reset() {
      setValues({ demand: "", leadTime: "", safetyDays: "" })
      setResult(null)
      setError(null)
   }

   function copyResult() {
      if (!result) return
      const text = [
         "Reorder Point Calculator — restaurantassociation.com",
         `Reorder Point: ${result.reorderPoint.toLocaleString()} units`,
         `Safety Stock: ${result.safetyStock.toLocaleString()} units`,
         `Max Stock Level: ${result.maxStockLevel.toLocaleString()} units`,
      ].join("\n")
      navigator.clipboard.writeText(text).catch(() => null)
      setResultCopied(true)
      setTimeout(() => setResultCopied(false), 2000)
   }

   async function shareLink() {
      const url = window.location.href
      if (navigator.share) {
         try {
            await navigator.share({
               title: "Reorder Point Calculator",
               text: "Calculate your ideal reorder point and safety stock levels.",
               url,
            })
            return
         } catch {
            // user cancelled or share failed — fall through to clipboard
         }
      }
      await navigator.clipboard.writeText(url).catch(() => null)
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2000)
   }

   return (
      <div className="p-6 md:p-8">
         <h2 className="mb-1 text-custom-3xl-bold text-gray-900">Calculate Your Reorder Point</h2>
         <p className="mb-6 text-custom-lg-normal text-gray-500">
            Enter your ingredient details below to calculate the ideal reorder threshold.
         </p>

         <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
            {FIELDS.map((f) => (
               <NumberInput
                  key={f.id}
                  id={f.id}
                  label={f.label}
                  tooltip={f.tooltip}
                  placeholder={f.placeholder}
                  value={values[f.id]}
                  onChange={(val) => handleChange(f.id, val)}
               />
            ))}
         </div>

         <Button
            variant="primary"
            className="mb-3 h-14 w-full gap-2 rounded-xl bg-[#EB5A23] text-[18px] font-bold text-white hover:bg-[#cf4e1e]"
            onClick={calculate}
         >
            <Calculator className="h-5 w-5" />
            Calculate Reorder Point
         </Button>

         <button
            type="button"
            onClick={reset}
            className="mb-6 w-full rounded-xl border border-gray-200 py-2.5 text-[18px] font-bold text-gray-600 transition hover:border-[#EB5A23] hover:text-[#EB5A23]"
         >
            ↺ Reset Calculator
         </button>

         {/* Result area */}
         {!result && !error && (
            <div className="flex flex-col items-center gap-3 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50 px-6 py-10 text-center">
               <svg
                  className="h-10 w-10 text-gray-300"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.5}
               >
                  <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                  <line x1="12" y1="22.08" x2="12" y2="12" />
               </svg>
               <p className="text-custom-base-bold text-gray-700">Ready to calculate</p>
               <p className="text-custom-sm-normal text-gray-400">Enter your figures above to see your reorder point.</p>
            </div>
         )}

         {error && (
            <div className="rounded-2xl border-2 border-red-200 bg-red-50 px-6 py-8 text-center text-sm font-semibold text-red-600">
               ⚠ {error}
            </div>
         )}

         {result && (
            <div className="rounded-2xl border-2 border-green-200 bg-white p-6 duration-300 animate-in fade-in slide-in-from-bottom-2">
               <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-gray-500">
                  Your Target Reorder Point
               </p>
               <p className="mb-5 font-serif text-5xl font-bold text-green-600">
                  {result.reorderPoint.toLocaleString()}{" "}
                  <span className="text-2xl font-semibold text-gray-500" style={{ fontFamily: "inherit" }}>
                     units
                  </span>
               </p>

               <div className="grid grid-cols-3 overflow-hidden rounded-xl border border-gray-100 bg-gray-50">
                  <ResultCell label="Reorder Point" value={result.reorderPoint.toLocaleString()} sub="units" />
                  <ResultCell label="Safety Stock" value={result.safetyStock.toLocaleString()} sub="units" />
                  <ResultCell label="Maximum Stock Level" value={result.maxStockLevel.toLocaleString()} sub="units" />
               </div>
            </div>
         )}

         {/* Action buttons */}
         <div className="mt-5 grid grid-cols-3 gap-3">
            {[
               { icon: <Copy className="h-4 w-4" />, label: resultCopied ? "Copied!" : "Copy Result", action: copyResult },
               { icon: <Share2 className="h-4 w-4" />, label: shareCopied ? "Link Copied!" : "Share Link", action: shareLink },
               { icon: <Printer className="h-4 w-4" />, label: "Print", action: () => window.print() },
            ].map(({ icon, label, action }) => (
               <button
                  key={label}
                  type="button"
                  onClick={action}
                  className="flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white py-3 text-sm font-semibold text-gray-600 transition hover:border-[#196EB9] hover:text-[#196EB9]"
               >
                  {icon}
                  <span className="hidden sm:inline">{label}</span>
               </button>
            ))}
         </div>
      </div>
   )
}
